import React, { useEffect, useState } from "react";
import { Plus, DollarSign, Package, Lock } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAgents, fetchAgentDetails } from "../../RTK/agents/GetAgents";
import Loader from "../../components/loader/Loader";
import AgentDetailsModal from "./agent-popup/AgentDetailsModal ";
import NewAgentModal from "./agent-popup/NewAgentModal";

import { toast } from "react-toastify"; 
import { usePermissions } from "../../contexts/PermissionsContext";
function Agent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions(); // ✅ إضافة

  // ✅ فحص الصلاحية
  const canManageAgents = hasPermission("manage_agents");
  const { agents } = useSelector((state) => state.allagents) || { agents: [] };
  const { agentDetails } = useSelector((state) => state.allagents) || {};
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isNewAgentModalOpen, setIsNewAgentModalOpen] = useState(false);

  // Add this function to handle successful agent creation
  const handleAgentCreated = async () => {
    setIsNewAgentModalOpen(false); // Close the modal
    await dispatch(fetchAgents()); // Refresh the agents list
  };

  useEffect(() => {
    // Fetch agents on mount
    dispatch(fetchAgents());
  }, [dispatch]);

  const handleAgentClick = (id) => {
    if (!canManageAgents) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل الوكلاء 🔒");
      return;
    }
    dispatch(fetchAgentDetails(id));
    setSelectedAgent(id);
  };

  const closeModal = () => {
    setSelectedAgent(null);
  };

  const openNewAgentModal = () => {
    if (!canManageAgents) {
      toast.error("ليس لديك صلاحية لإضافة وكلاء جدد 🔒");
      return;
    }
    setIsNewAgentModalOpen(true);
  };

  const closeNewAgentModal = () => {
    setIsNewAgentModalOpen(false);
  };

  const StatusBadge = ({ is_active }) => (
    <div
      className={`px-2 py-1 text-sm rounded-full ${
        is_active
          ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
          : "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
      }`}
    >
      {is_active ? "Active" : "Inactive"}
    </div>
  );

  // Total Balance Summary Card
  const TotalBalanceCard = ({ totalBalance }) => (
    <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 p-6 mb-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white mb-1">
            إجمالي رصيد الوكلاء
          </h2>
          <p className="text-gray-400 text-sm">Total Agents Balance</p>
        </div>
        <div className="p-3 rounded-lg bg-yellow-500/10 text-yellow-500">
          <DollarSign className="w-6 h-6" />
        </div>
      </div>
      <div className="mt-4">
        <h3 className="text-3xl font-bold text-white">{totalBalance ?? 0}$</h3>
      </div>
    </div>
  );

  const SimpleAgentCard = ({ agent }) => {
    if (agent.name === "lowest" || agent.name === "Daily Card") {
      return null;
    }

    return (
      <div 
        className={`bg-gray-800 backdrop-blur-sm rounded-xl overflow-hidden border border-gray-700 transition-all duration-300 shadow-md relative ${
          canManageAgents 
            ? "hover:border-primary cursor-pointer" 
            : "opacity-70 cursor-not-allowed"
        }`}
      >
        {/* ✅ أيقونة قفل لو مافيش صلاحية */}
        {!canManageAgents && (
          <div className="absolute top-2 left-2 bg-slate-700/90 p-1.5 rounded-lg z-10">
            <Lock className="w-3 h-3 text-slate-400" />
          </div>
        )}

        <div
          onClick={() => handleAgentClick(agent.id)}
          className="p-4 flex items-center gap-4"
        >
          <img
            src={agent.icon || "avatar.png"}
            alt={agent.name}
            className={`w-12 h-12 rounded-full object-cover border-4 
              ${agent.is_active ? "border-green-900" : "border-yellow-900"}`}
          />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-1 truncate">
              {agent.name}
            </h3>
            <StatusBadge is_active={agent.is_active} />
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-400 text-left">الرصيد</p>
            <p className="text-lg font-bold text-white mt-4">
              {agent.agent_balance ?? 0}$
            </p>
          </div>
        </div>

        {/* Products Button */}
        <div className="border-t border-gray-700 p-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!canManageAgents) {
                toast.error("ليس لديك صلاحية لإدارة منتجات الوكلاء 🔒");
                return;
              }
              navigate(`/agent/${agent.id}/products`);
            }}
            disabled={!canManageAgents}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
              canManageAgents
                ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                : "bg-slate-800 text-slate-400 cursor-not-allowed"
            }`}
          >
            <Package className="w-4 h-4" />
            <span className="text-sm font-medium">إدارة المنتجات</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen text-white p-6" dir="rtl">
      <div className="flex justify-between items-center mb-8">
        {/* ✅ زر إضافة وكيل - محمي */}
        {canManageAgents ? (
          <button
            type="button"
            className="flex items-center gap-2 px-5 py-3 rounded-md bg-gray-800 text-white font-medium 
              hover:bg-gray-900 transition-all"
            onClick={openNewAgentModal}
          >
            <Plus className="w-5 h-5" />
            إضافة وكيل جديد
          </button>
        ) : (
          <div className="flex items-center gap-2 px-5 py-3 bg-slate-800/50 rounded-md border border-slate-700">
            <Lock className="w-5 h-5 text-slate-500" />
            <span className="text-slate-400">
              ليس لديك صلاحية إضافة وكلاء
            </span>
          </div>
        )}
      </div>

      {/* Add Total Balance Card */}
      {agents?.total_balance && (
        <TotalBalanceCard totalBalance={agents.total_balance} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
        {agents?.agents?.length > 0 ? (
          agents.agents.map((agent) => (
            <SimpleAgentCard key={agent.id} agent={agent} />
          ))
        ) : (
          <Loader />
        )}
      </div>

      {/* ✅ Modal يظهر فقط لو عنده صلاحية */}
      {selectedAgent && agentDetails && canManageAgents && (
        <AgentDetailsModal agentDetails={agentDetails} onClose={closeModal} />
      )}

      {isNewAgentModalOpen && canManageAgents && (
        <NewAgentModal
          onClose={closeNewAgentModal}
          onSuccess={handleAgentCreated}
        />
      )}
    </div>
  );
}

export default Agent;
